import * as React from 'react'
import styled from 'styled-components'
import Layout from 'containers/Layout'
import Container from 'theme/atoms/Container'
import Breadcrumbs, { Crumb } from 'theme/atoms/Breadcrumbs'
import theme from 'theme'
import MobileBacklink from './MobileBacklink'
import DesktopSidebar from './DesktopSidebar'
import MobileDropdown from './MobileDropdown'
import useAccount from 'hooks/account/useAccount'
import { navigate } from 'gatsby'
import Button from 'theme/atoms/Button'
import useTranslations from 'hooks/useTranslations'

export type PageType = 'orders' | 'addresses' | 'dashboard' | 'personal'

type Props = {
  activePage: PageType
  breadcrumbs: Crumb[]
  children: any
  title?: string
  backLink?: string
  backLabel?: string
  description?: React.ReactElement
  'data-cy-ctx'?: string
}

export default function AccountDashboard(props: Props) {
  const t = useTranslations<'app-AccountDashboard'>()
  const account = useAccount()

  React.useEffect(() => {
    if (account.isFetching) return
    if (!account.loggedIn) navigate('/account/login/')
  }, [account.isFetching, account.loggedIn])

  return (
    <Layout pageContext={{ type: 'Other', ready: true }}>
      <Container>
        <Breadcrumbs isFetching={false} breadcrumbs={props.breadcrumbs} />
        <Wrapper
          data-cy-ctx={props['data-cy-ctx'] || 'app/AccountDashboard'}
          className="AccountDashboard"
          $showMobileBack={props.activePage !== 'dashboard'}
        >
          <MobileBacklink
            backLabel={props.backLabel || t('dashboard_return_text')}
            backLink={props.backLink || '/account/'}
          />
          <MobileDropdown activePage={props.activePage} />
          <DesktopSidebar activePage={props.activePage} />
          <div className="account-content">
            {props.title && (
              // eslint-disable-next-line @kaminrunde/firescout/jsx-expression-needs-state
              <h1>{props.title}</h1>
            )}
            {props.description && (
              <div className="description" data-cy-state="description">
                {props.description}
              </div>
            )}
            {props.children}
            {props.activePage !== 'dashboard' && (
              <div className="back-link" data-cy-state="show-back-btn">
                <Button
                  variation="secondary"
                  icon="arrow-right"
                  to={props.backLink || '/account/'}
                  data-cy-handle="back-btn"
                >
                  {props.backLabel || t('dashboard_return_text')}
                </Button>
              </div>
            )}
          </div>
        </Wrapper>
      </Container>
    </Layout>
  )
}

const Wrapper = styled.div<{ $showMobileBack: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing('m')};
  margin-top: ${theme.spacing('m')};
  @media (min-width: ${theme.ms.LG}px) {
    flex-direction: row;
    margin-top: ${theme.spacing('l')};
  }
  > .account-content {
    width: 100%;

    > h1 {
      ${theme.ty('rc-3xl')};
      margin-bottom: ${theme.spacing('m')};
      color: ${theme.colors.primary};
      @media (min-width: ${theme.ms.LG}px) {
        ${theme.ty('rc-4xl')};
      }
    }
    > .description {
      ${theme.ty('rc-base')};
      > span > span > .description-link {
        ${theme.ty('rc-base', '700')};
        text-decoration: underline;
      }
    }

    > .back-link {
      display: flex;
      justify-content: center;
      margin-top: ${theme.spacing('l')};
      > .Button {
        width: 100%;
        @media (min-width: ${theme.ms.MD}px) {
          width: auto;
        }
        svg {
          transform: rotate(180deg);
          * {
            stroke: black;
          }
        }
      }
    }
  }

  > .MobileBacklink {
    display: ${(p) => (p.$showMobileBack ? 'block' : 'none')};
    @media (min-width: ${theme.ms.LG}px) {
      display: none;
    }
  }
  > .MobileDropdown {
    display: ${(p) => (p.$showMobileBack ? 'none' : 'block')};
    @media (min-width: ${theme.ms.LG}px) {
      display: none;
    }
  }
  > .DesktopSidebar {
    display: none;
    @media (min-width: ${theme.ms.LG}px) {
      display: block;
      width: 310px;
    }
  }

  @media (min-width: ${theme.ms.LG}px) {
    --container-size: calc(100vw - 310px - ${theme.spacing('m')} * 2);
  }

  @media (min-width: 1340px) {
    --container-size: calc(1300px - 310px);
  }
`
